import { findRelatedElementByElement } from "../DOMutils";
import animateValue, { updateHeight } from "../animateValue";
import { addKeydownEventListener } from "../keyboard";
import ExternalEvents from "./ExternalEvents";
import { setAriaExapndedAttribute, setInertAttribute, setExpendableAttributes } from "../accessibility";

export default class Dropdown extends ExternalEvents {
    constructor(el) {
        super(["valueChange"]);
        this.el = el;
        this.isOpened = false;
        this.currentValue = null;
        this.defaultValue = null;
        this.toggle = this.el.querySelector(".dropdown__toggle");
        this.toggleValue = this.el.querySelector(".dropdown__toggle-value");
        this.content = this.el.querySelector(".dropdown__content");
        this.handleOutsideClickBinded = this.handleOutsideClick.bind(this);
        this.focusedItemIdx = null;

        this.setDefaultValue();

        if (this.toggle && this.content) setExpendableAttributes(this.toggle, this.content);
    }

    toggleDropdown() {
        this.isOpened = !this.isOpened;
        this.el.classList.toggle("dropdown--active");

        animateValue(
            this.content,
            updateHeight,
            this.content.offsetHeight,
            !this.isOpened ? 0 : this.content.scrollHeight > 300 ? 300 : this.content.scrollHeight,
            150
        );
        this.setOutsideClickEventListener();

        setAriaExapndedAttribute(this.toggle, this.isOpened);
        setInertAttribute(this.content, this.isOpened);
    }

    handleOutsideClick(e) {
        const isInside = findRelatedElementByElement(e.target, "parentElement", this.el);

        if (isInside) return;

        this.toggleDropdown();
    }

    setOutsideClickEventListener() {
        this.isOpened
            ? document.addEventListener("click", this.handleOutsideClickBinded, false)
            : document.removeEventListener("click", this.handleOutsideClickBinded, false);
    }

    setValue(value, e) {
        if (value === this.currentValue) return;

        this.currentValue = value;
        
        // Get the text from the clicked item
        const item = e && e.target ? e.target : this.content.querySelector(`[data-value="${value}"]`);
        const displayText = item ? (item.textContent || item.innerText) : value;
        
        this.toggleValue ? (this.toggleValue.innerText = displayText) : (this.toggle.innerText = displayText);
        this.runExternalEventHandlers("valueChange", e);
    }

    handleContentClick(e) {
        const value = e.target.dataset.value;

        if (value === undefined) return;

        this.toggleDropdown();

        this.setValue(value, e);
    }

    handleEscapeEvent() {
        if (!this.isOpened) return;

        this.toggleDropdown();
        this.toggle.focus();
        this.focusedItemIdx = null;
    }

    handleArrowsEvent(e) {
        if (!this.isOpened) return;

        const isTab = e.key === "Tab";

        if (!isTab) e.preventDefault();

        const items = [this.toggle, ...this.content.querySelectorAll(".dropdown__item")];

        this.focusedItemIdx =
            this.focusedItemIdx === null
                ? 0
                : e.key === "ArrowUp" || (e.shiftKey && isTab)
                ? this.focusedItemIdx - 1
                : e.key === "ArrowDown" || (!e.shiftKey && isTab)
                ? this.focusedItemIdx + 1
                : null;

        if (this.focusedItemIdx > items.length - 1) this.focusedItemIdx = 0;

        if (this.focusedItemIdx < 0) this.focusedItemIdx = items.length - 1;

        if (!isTab && items[this.focusedItemIdx]) items[this.focusedItemIdx].focus();
    }

    setEventListeners() {
        this.toggle.addEventListener("click", this.toggleDropdown.bind(this));
        this.content.addEventListener("click", this.handleContentClick.bind(this));
        addKeydownEventListener(this.el, {
            Escape: [this.handleEscapeEvent.bind(this)],
            ArrowUp: [this.handleArrowsEvent.bind(this)],
            ArrowDown: [this.handleArrowsEvent.bind(this)],
            Tab: [this.handleArrowsEvent.bind(this)],
        });
    }

    setDefaultValue() {
        const defaultItem = this.content.querySelector(".dropdown__item[data-default='true']");

        if (!defaultItem || !defaultItem.dataset.value) return;

        this.currentValue = defaultItem.dataset.value;
        this.defaultValue = defaultItem.dataset.value;
    }

    reset() {
        const defaultItem = this.content.querySelector(".dropdown__item[data-default='true']");
        if (defaultItem) {
            const defaultText = defaultItem.textContent || defaultItem.innerText;
            const defaultValue = defaultItem.dataset.value;
            const previousValue = this.currentValue;
            
            this.currentValue = defaultValue;
            this.defaultValue = defaultValue;
            this.toggleValue ? (this.toggleValue.innerText = defaultText) : (this.toggle.innerText = defaultText);
            
            console.log('[Dropdown] Reset:', {
                previousValue,
                newValue: this.currentValue,
                defaultText,
                changed: previousValue !== defaultValue
            });
            
            // Only trigger valueChange if value actually changed
            if (previousValue !== defaultValue) {
                // Create a mock event object with target property
                const mockEvent = { target: defaultItem };
                this.runExternalEventHandlers("valueChange", mockEvent);
            }
        }
    }

    run() {
        if (!this.toggle || !this.content) return;

        this.setEventListeners();
    }
}
